/**
 * Chat Error Boundary Component
 *
 * Comprehensive error boundary for chat operations extending PermissionErrorBoundary patterns.
 * Provides graceful error handling, recovery mechanisms, and user-friendly feedback.
 */

import React, { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  AlertTriangle,
  RefreshCw,
  MessageSquare,
  Wifi,
  Shield,
  Clock,
  Bug,
  ExternalLink,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  ChatError,
  isChatAuthenticationError,
  isChatRateLimitError,
  isChatStreamingError,
  isChatNetworkError,
  isChatValidationError,
  isChatThreadError,
} from "@/lib/errors/chatErrors";
import { logChatError, logChatRecovery } from "@/lib/errors/chatErrorLogger";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  onRetry?: () => void;
  showDebugInfo?: boolean;
  className?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  errorId?: string;
  retryCount: number;
  isRetrying: boolean;
}

export class ChatErrorBoundary extends Component<Props, State> {
  private retryTimeoutId?: NodeJS.Timeout;
  private maxRetries = 3;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      retryCount: 0,
      isRetrying: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error with context
    const errorId = logChatError(error, {
      additionalData: {
        componentStack: errorInfo.componentStack,
        errorBoundary: "ChatErrorBoundary",
      },
    });

    this.setState({
      error,
      errorInfo,
      errorId,
    });

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);

    // Log to console in development
    if (process.env.NODE_ENV === "development") {
      console.error("ChatErrorBoundary caught an error:", error, errorInfo);
    }
  }

  componentWillUnmount() {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }
  }

  handleRetry = async () => {
    const { error, errorId, retryCount } = this.state;

    if (retryCount >= this.maxRetries) {
      return;
    }

    this.setState({ isRetrying: true });

    try {
      // Determine retry delay based on error type
      let retryDelay = 1000; // Default 1 second

      if (error instanceof ChatError) {
        retryDelay = error.recovery.retryDelay || 1000;
      }

      // Wait for retry delay
      await new Promise((resolve) => {
        this.retryTimeoutId = setTimeout(resolve, retryDelay);
      });

      // Reset error state
      this.setState({
        hasError: false,
        error: undefined,
        errorInfo: undefined,
        retryCount: retryCount + 1,
        isRetrying: false,
      });

      // Log recovery attempt
      if (errorId) {
        logChatRecovery(errorId, true, "manual_retry");
      }

      // Call custom retry handler if provided
      this.props.onRetry?.();
    } catch (retryError) {
      console.error("Retry failed:", retryError);

      if (errorId) {
        logChatRecovery(errorId, false, "manual_retry");
      }

      this.setState({ isRetrying: false });
    }
  };

  getErrorIcon = (error: Error) => {
    if (isChatAuthenticationError(error)) return Shield;
    if (isChatRateLimitError(error)) return Clock;
    if (isChatStreamingError(error)) return Wifi;
    if (isChatNetworkError(error)) return Wifi;
    if (isChatValidationError(error)) return AlertTriangle;
    if (isChatThreadError(error)) return MessageSquare;
    return Bug;
  };

  getErrorColor = (error: Error) => {
    if (error instanceof ChatError) {
      switch (error.severity) {
        case "critical":
          return "destructive";
        case "high":
          return "destructive";
        case "medium":
          return "default";
        case "low":
          return "secondary";
      }
    }
    return "destructive";
  };

  getUserFriendlyMessage = (error: Error): string => {
    if (error instanceof ChatError) {
      return error.getUserMessage();
    }

    // Fallback messages for non-ChatError instances
    if (isChatAuthenticationError(error)) {
      return "Please sign in to continue chatting.";
    }
    if (isChatRateLimitError(error)) {
      return "Too many requests. Please wait a moment before trying again.";
    }
    if (isChatStreamingError(error)) {
      return "Connection issue occurred. Retrying...";
    }
    if (isChatNetworkError(error)) {
      return "Network connection issue. Please check your internet connection.";
    }
    if (isChatValidationError(error)) {
      return "Invalid input. Please check your message and try again.";
    }
    if (isChatThreadError(error)) {
      return "Unable to access this conversation. Please try again.";
    }

    return "An unexpected error occurred. Please try again.";
  };

  canRetry = (error: Error): boolean => {
    if (error instanceof ChatError) {
      return error.isRetryable() && this.state.retryCount < this.maxRetries;
    }

    // Default retry logic for non-ChatError instances
    return (
      !isChatAuthenticationError(error) &&
      !isChatValidationError(error) &&
      this.state.retryCount < this.maxRetries
    );
  };

  render() {
    if (this.state.hasError && this.state.error) {
      const { error } = this.state;
      const { fallback, showDebugInfo = process.env.NODE_ENV === "development" } = this.props;

      // Use custom fallback if provided
      if (fallback) {
        return fallback;
      }

      const ErrorIcon = this.getErrorIcon(error);
      const errorColor = this.getErrorColor(error);
      const userMessage = this.getUserFriendlyMessage(error);
      const canRetry = this.canRetry(error);

      return (
        <div className={cn("space-y-4 p-4", this.props.className)}>
          <Alert variant={errorColor as any}>
            <ErrorIcon className="h-4 w-4" />
            <AlertTitle className="flex items-center gap-2">
              Chat Error
              {error instanceof ChatError && (
                <Badge variant="outline" className="text-xs">
                  {error.severity}
                </Badge>
              )}
            </AlertTitle>
            <AlertDescription className="mt-2">
              <p className="mb-3">{userMessage}</p>

              {/* Action buttons */}
              <div className="flex flex-wrap gap-2">
                {canRetry && (
                  <Button
                    onClick={this.handleRetry}
                    disabled={this.state.isRetrying}
                    size="sm"
                    variant="outline"
                  >
                    {this.state.isRetrying ? (
                      <>
                        <RefreshCw className="mr-1 h-3 w-3 animate-spin" />
                        Retrying...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="mr-1 h-3 w-3" />
                        Try Again
                      </>
                    )}
                  </Button>
                )}

                <Button onClick={() => window.location.reload()} size="sm" variant="outline">
                  <RefreshCw className="mr-1 h-3 w-3" />
                  Refresh Page
                </Button>

                {isChatAuthenticationError(error) && (
                  <Button onClick={() => (window.location.href = "/sign-in")} size="sm">
                    <Shield className="mr-1 h-3 w-3" />
                    Sign In
                  </Button>
                )}
              </div>

              {/* Retry count indicator */}
              {this.state.retryCount > 0 && (
                <p className="mt-2 text-xs text-muted-foreground">
                  Retry attempts: {this.state.retryCount}/{this.maxRetries}
                </p>
              )}

              {/* Debug information */}
              {showDebugInfo && (
                <details className="mt-4">
                  <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
                    Debug Information
                  </summary>
                  <div className="mt-2 rounded bg-muted p-2 font-mono text-xs">
                    <div>
                      <strong>Error:</strong> {error.name}
                    </div>
                    <div>
                      <strong>Message:</strong> {error.message}
                    </div>
                    {error instanceof ChatError && (
                      <>
                        <div>
                          <strong>Code:</strong> {error.code}
                        </div>
                        <div>
                          <strong>Severity:</strong> {error.severity}
                        </div>
                        <div>
                          <strong>Error ID:</strong> {this.state.errorId}
                        </div>
                      </>
                    )}
                    {error.stack && (
                      <details className="mt-2">
                        <summary className="cursor-pointer">Stack Trace</summary>
                        <pre className="mt-1 max-h-32 overflow-auto text-xs">{error.stack}</pre>
                      </details>
                    )}
                  </div>
                </details>
              )}
            </AlertDescription>
          </Alert>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Higher-order component for wrapping components with chat error boundary
 */
export function withChatErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  options: {
    fallback?: ReactNode;
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
    onRetry?: () => void;
    showDebugInfo?: boolean;
  } = {}
) {
  return function WrappedComponent(props: P) {
    return (
      <ChatErrorBoundary {...options}>
        <Component {...props} />
      </ChatErrorBoundary>
    );
  };
}

/**
 * Hook for handling chat errors gracefully
 */
export function useChatErrorHandler() {
  const handleChatError = (error: Error, context?: string) => {
    const errorId = logChatError(error, {
      additionalData: { context },
    });

    console.error(`Chat error${context ? ` in ${context}` : ""}:`, error);

    return {
      errorId,
      message: error instanceof ChatError ? error.getUserMessage() : "An error occurred",
      canRetry: error instanceof ChatError ? error.isRetryable() : true,
      severity: error instanceof ChatError ? error.severity : "medium",
    };
  };

  return { handleChatError };
}
